import { useCallback, useEffect, useId, useRef, useState } from "react";

export const HOVER_OPEN_DELAY_MS = 400;
export const HOVER_INTERACTIVE_CLOSE_DELAY_MS = 120;

// Trigger and popup can live in different DOM trees (portals). Only keyboard
// focus is sticky; a pointer click must not turn a hover into retained focus.
export function useHoverIntent({ enabled = true, interactive = false, onOpen, onClose } = {}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const current = useRef({});
  current.current = { enabled, interactive, onOpen, onClose };
  const state = useRef({ trigger: false, popup: false, triggerFocus: false, popupFocus: false, pointerFocus: false, open: false, dismissed: false });
  const timers = useRef({});
  const cancel = useCallback(() => {
    clearTimeout(timers.current.open);
    clearTimeout(timers.current.close);
  }, []);
  const commit = useCallback((value) => {
    if (state.current.open === value) return;
    state.current.open = value;
    setOpen(value);
    if (value) current.current.onOpen?.();
    else current.current.onClose?.();
  }, []);
  const dismiss = useCallback(() => {
    cancel();
    state.current.dismissed = true;
    state.current.popup = false;
    state.current.popupFocus = false;
    state.current.popupElement = null;
    commit(false);
  }, [cancel, commit]);
  // A chart exit/data replacement ends the entire interaction, including
  // pointer-originated focus. Ordinary dismissal keeps its existing contract.
  const reset = useCallback(() => {
    dismiss();
    Object.assign(state.current, { trigger: false, triggerFocus: false, pointerFocus: false });
  }, [dismiss]);
  const show = useCallback(() => {
    cancel();
    if (current.current.enabled && !state.current.dismissed) commit(true);
  }, [cancel, commit]);
  const openImmediately = useCallback(() => {
    state.current.dismissed = false;
    show();
  }, [show]);
  const leave = useCallback((immediate = false) => {
    cancel();
    const s = state.current;
    if (s.trigger || s.popup || s.triggerFocus || s.popupFocus) return;
    if (!immediate && current.current.interactive && s.open) {
      timers.current.close = setTimeout(() => commit(false), HOVER_INTERACTIVE_CLOSE_DELAY_MS);
    } else commit(false);
  }, [cancel, commit]);
  const enter = useCallback(() => {
    cancel();
    const s = state.current;
    s.trigger = true;
    s.dismissed = false;
    if (!current.current.enabled || s.open) return;
    timers.current.open = setTimeout(show, HOVER_OPEN_DELAY_MS);
  }, [cancel, show]);
  const pointerDown = useCallback(() => {
    state.current.pointerFocus = true;
    state.current.triggerFocus = false;
    state.current.popupFocus = false;
  }, []);
  const focus = useCallback((part, event) => {
    if (!current.current.enabled || !event.currentTarget.contains(event.target)) return;
    if (event.currentTarget.contains(event.relatedTarget)) return;
    if (state.current.pointerFocus) return;
    state.current[part] = true;
    state.current.dismissed = false;
    show();
  }, [show]);
  const blur = useCallback((part, event) => {
    if (!event.currentTarget.contains(event.target)) return;
    if (event.currentTarget.contains(event.relatedTarget)) return;
    state.current[part] = false;
    const other = part === "triggerFocus" ? "popup" : "trigger";
    if (current.current.interactive && !state.current.pointerFocus
      && event.relatedTarget?.closest?.(`[data-hover-intent-${other}]`)?.getAttribute(`data-hover-intent-${other}`) === id) {
      state.current[`${other}Focus`] = true;
      return;
    }
    state.current.pointerFocus = false;
    leave(true);
  }, [leave, id]);
  const keyDown = useCallback((event) => {
    if (event.key === "Escape") {
      dismiss();
      event.stopPropagation();
    } else {
      state.current.pointerFocus = false;
    }
  }, [dismiss]);

  useEffect(() => {
    if (!enabled) {
      dismiss();
      Object.assign(state.current, { trigger: false, triggerFocus: false, pointerFocus: false });
    }
  }, [enabled, dismiss]);
  useEffect(() => () => {
    cancel();
    if (state.current.open) current.current.onClose?.();
  }, [cancel]);

  return {
    open: enabled && open, dismiss, reset, show: openImmediately, enter,
    triggerProps: {
      "data-hover-intent-trigger": id,
      onMouseEnter: enter,
      onMouseLeave: (event) => {
        state.current.trigger = false;
        // A popup inside the trigger leaves with the whole hover zone. Portals
        // have their own independent exit and must keep their transfer grace.
        if (event?.currentTarget.contains(state.current.popupElement)) state.current.popup = false;
        leave();
      },
      onPointerDown: pointerDown,
      onMouseDown: pointerDown,
      onFocus: (event) => focus("triggerFocus", event),
      onBlur: (event) => blur("triggerFocus", event),
      onKeyDown: keyDown,
    },
    popupProps: {
      "data-hover-intent-popup": id,
      onMouseEnter: (event) => {
        state.current.popup = true;
        state.current.popupElement = event.currentTarget;
        cancel();
      },
      onMouseLeave: () => { state.current.popup = false; leave(); },
      onPointerDown: pointerDown,
      onFocus: (event) => focus("popupFocus", event),
      onBlur: (event) => blur("popupFocus", event),
      onKeyDown: keyDown,
    },
  };
}
